import type { FileListAction, FilesListItem } from '../types';

import { cn } from 'cn';
import { Button } from '~/molecules';

import { truncate } from '@vestigas/utils';

import { FileTypeSymbol } from './FileTypeSymbol';
import { Thumbnail } from './Thumbnail';

/** Min/max size for square grid cards. Uses repeat(auto-fill, minmax(MIN, MAX)). */
const GRID_ITEM_SIZE_CLASSES = {
  sm: 'grid-cols-[repeat(auto-fill,minmax(9rem,1fr))]',
  md: 'grid-cols-[repeat(auto-fill,minmax(11rem,1fr))]',
  lg: 'grid-cols-[repeat(auto-fill,minmax(13rem,1fr))]',
} as const;

const GRID_THUMBNAIL_SIZE = {
  sm: 'md',
  md: 'lg',
  lg: 'lg',
} as const;

type ItemSize = keyof typeof GRID_ITEM_SIZE_CLASSES;

type ItemClassName = string | ((item: FilesListItem) => string);

type FileItemBaseProps = {
  item: FilesListItem;
  itemClassName?: ItemClassName;
  resolvedActions: FileListAction[];
};

type FileProgressBarProps = Pick<FileItemBaseProps, 'item'> & {
  className?: string;
  error?: unknown;
};

type ActionButtonsProps = Omit<FileItemBaseProps, 'itemClassName'>;

type ListItemProps = FileItemBaseProps;

type GridItemProps = FileItemBaseProps & {
  itemSize: ItemSize;
  onItemClick?: (item: FilesListItem) => void;
};

type FilesListProps = {
  readonly actions?: FileListAction[];
  readonly displayAs?: 'grid' | 'list';
  readonly itemClassName?: ItemClassName;
  readonly itemSize?: ItemSize;
  readonly items: FilesListItem[];
  readonly onItemClick?: (item: FilesListItem) => void;
} & ComponentStyling;

function getFileTypeLabel(item: FilesListItem): string {
  const fileExtension = item.name.split('.').pop()?.trim();
  if (fileExtension) {
    return fileExtension.toUpperCase();
  }

  const mimeSubType = item.type?.split('/').pop()?.trim();
  if (mimeSubType) {
    return mimeSubType.toUpperCase();
  }

  return 'FILE';
}

function getFileTypeSymbol(item: FilesListItem): string {
  const normalized = getFileTypeLabel(item).replace(/[^A-Z0-9]/gi, '');
  if (!normalized) {
    return 'FILE';
  }

  return normalized.slice(0, 4).toUpperCase();
}

function isImageFile(item: FilesListItem): boolean {
  return Boolean(item.type?.startsWith('image/'));
}

function FileProgressBar({ className, error, item }: FileProgressBarProps) {
  if (item.progress === undefined) return null;

  return (
    <progress
      max="100"
      className={cn(
        'absolute right-0 bottom-0 left-0 block h-1 w-full appearance-none overflow-hidden bg-transparent [&::-webkit-progress-bar]:bg-transparent',
        {
          '[&::-moz-progress-bar]:bg-green-500 [&::-webkit-progress-value]:bg-green-500':
            item.progress.uploadComplete,
          '[&::-moz-progress-bar]:bg-red-500 [&::-webkit-progress-value]:bg-red-500':
            error,
          '[&::-moz-progress-bar]:bg-blue-500 [&::-webkit-progress-value]:bg-blue-500':
            !item.progress.uploadComplete && !error,
        },
        className,
      )}
      value={item.progress.percentage ?? 0}
    />
  );
}

function ActionButtons({ item, resolvedActions }: ActionButtonsProps) {
  return (
    <>
      {resolvedActions.map((action, index) => (
        <Button
          key={action.id ?? `action-${index}`}
          aria-label={`action-${index}`}
          buttonStyle="link"
          className="transition-colors duration-300 hover:text-tiga-action-lighter"
          disabled={action.disabled}
          icon={action.icon}
          onClick={() => action.onClick(item)}
          text={action.text}
        />
      ))}
    </>
  );
}

function ListItem({ item, itemClassName, resolvedActions }: ListItemProps) {
  const resolvedItemClassName =
    typeof itemClassName === 'function' ? itemClassName(item) : itemClassName;

  return (
    <div
      className={cn(
        'relative flex h-8 flex-row items-center gap-2 rounded-sm whitespace-nowrap transition-colors duration-200 ease-out hover:bg-tiga-action-lighter/13',
        resolvedItemClassName,
      )}
    >
      <Thumbnail file={item} />
      <span className="truncate text-sm">{truncate(item.name, 40, 12)}</span>
      <div className="ml-auto flex items-center gap-1">
        <ActionButtons item={item} resolvedActions={resolvedActions} />
      </div>
      <FileProgressBar
        className="rounded-full"
        error={item.error}
        item={item}
      />
    </div>
  );
}

function GridItem({
  item,
  itemClassName,
  itemSize,
  onItemClick,
  resolvedActions,
}: GridItemProps) {
  const thumbnailSize = GRID_THUMBNAIL_SIZE[itemSize];
  const resolvedItemClassName =
    typeof itemClassName === 'function' ? itemClassName(item) : itemClassName;
  const fileTypeSymbol = getFileTypeSymbol(item);
  const canShowImagePreview =
    isImageFile(item) && Boolean(item.preview ?? item.data);

  return (
    <div
      className={cn(
        'relative flex h-full min-h-48 w-full flex-col overflow-hidden rounded-md border border-tiga-border-lighter bg-white text-left transition-colors duration-300 ease-out hover:border-tiga-action-lighter hover:bg-tiga-action-lighter/13 focus:outline-none focus-visible:outline-none',
        {
          'cursor-pointer': onItemClick,
        },
        resolvedItemClassName,
      )}
      onClick={() => onItemClick?.(item)}
      onKeyDown={(event) => {
        if (!onItemClick) {
          return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onItemClick(item);
        }
      }}
      role={onItemClick ? 'button' : undefined}
      tabIndex={onItemClick ? 0 : undefined}
    >
      {Boolean(resolvedActions.length) && (
        <div
          className="absolute top-2 right-2 z-10 flex items-center justify-end gap-1"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <ActionButtons item={item} resolvedActions={resolvedActions} />
        </div>
      )}
      <div className="relative aspect-4/3 w-full min-w-0 overflow-hidden bg-white contain-paint">
        {canShowImagePreview ? (
          <Thumbnail
            className="h-full w-full rounded-none object-cover"
            file={item}
            size={thumbnailSize}
          />
        ) : (
          <FileTypeSymbol className="size-full" label={fileTypeSymbol} />
        )}
      </div>
      <div className="min-h-12 w-full px-2 py-1.5">
        <span className="[display:-webkit-box] overflow-hidden text-sm leading-tight wrap-anywhere text-tiga-text-default [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
          {item.name}
        </span>
      </div>
      <FileProgressBar
        className="rounded-b-xl"
        error={item.error}
        item={item}
      />
    </div>
  );
}

/**
 * A list of files with thumbnails and optional actions.
 * @param actions - The actions to show for each file.
 * @param displayAs - Layout variant: "list" (default) or "grid".
 * @param itemClassName - Class name(s) applied to each item. Accepts a string or a callback `(item) => string` for per-item styling.
 * @param itemSize - Size of the items in the list: "sm" | "md" | "lg".
 * @param items - The files to show in the list.
 */
export function FilesList({
  actions,
  className,
  displayAs = 'list',
  itemClassName,
  itemSize = 'md',
  items,
  onItemClick,
  style,
}: FilesListProps) {
  if (!items.length) {
    return null;
  }

  const isGrid = displayAs === 'grid';

  return (
    <ul
      className={cn(
        'overflow-y-auto',
        {
          'space-y-0.5': !isGrid,
          'grid gap-3': isGrid,
          [GRID_ITEM_SIZE_CLASSES[itemSize]]: isGrid,
        },
        className,
      )}
      style={style}
    >
      {items.map((item) => (
        <li key={item.id}>
          {isGrid ? (
            <GridItem
              item={item}
              itemClassName={itemClassName}
              itemSize={itemSize}
              onItemClick={onItemClick}
              resolvedActions={actions ?? []}
            />
          ) : (
            <ListItem
              item={item}
              itemClassName={itemClassName}
              resolvedActions={actions ?? []}
            />
          )}
        </li>
      ))}
    </ul>
  );
}
